import type { Event, EventHint } from '@sentry/vue';

import { extractApiErrorTags } from '@paybis/frontend-common-lib/src/plugins/apiErrorTags';

import scrubRequestData from '@/utilities/sentryScrubber';
import {
  denyUrls,
  ignoredErrorMessages,
  filterUnwantedExceptions,
} from './sentry-ignore-config';

// Broad first-party domain patterns for distributed tracing (sentry-trace/baggage headers).
// Intentionally separate from getAllowedNetworkUrls(): replay uses a narrower env-based
// allow-list for request/response body capture, not the same policy as trace propagation.
// CDN and server-side GTM hosts are excluded: they must stay simple CORS requests —
// sentry-trace/baggage are not CORS-safelisted, so they force a preflight that neither
// CDN CORS nor the Google Tag Manager server container answers, and the request is
// dropped. gtm.* is our domain but not our backend (a first-party-hosted Google
// container), so a propagated trace would never reach Sentry anyway.
export const tracePropagationTargets = [
  /^https:\/\/(?!cdn[.-]|gtm[.-])([a-z0-9-]+\.)*paybis\.com(?:[:/]|$)/,
  /^https:\/\/(?!cdn[.-]|gtm[.-])([a-z0-9-]+\.)*techhub\.cloud(?:[:/]|$)/,
];

export const tracesSampleRate = DEPLOYMENT_ENV === 'dev' ? 1.0 : 0.25; // eslint-disable-line no-undef

export const baseConfig = {
  environment: DEPLOYMENT_ENV,  
  dsn: SENTRY_DSN,
  release: RELEASE,
  maxBreadcrumbs: 30, // By default, sentry stores 100 breadcrumbs
  tracesSampleRate,
  tracePropagationTargets,
  beforeSend: (event: Event, hint?: EventHint): Event | null => {
    if (!filterUnwantedExceptions(event, hint?.originalException)) {
      return null;
    }

    // XXX: scrub sensitive data
    if (event.request && event.request.data) {
      event.request.data = scrubRequestData(event.request.data);
    }

    // Safety net for events that bypass the shared captureException wrapper
    // (global handler, direct Sentry calls). Overlap with the wrapper's
    // withScope tagging writes identical values, so it is idempotent.
    const apiTags = extractApiErrorTags(hint?.originalException);
    if (apiTags) {
      event.tags = { ...event.tags, ...apiTags };
    }

    return event;
  },
  ignoreErrors: ignoredErrorMessages,
  denyUrls,
};

export function getAllowedNetworkUrls() {
  return [
    window.location.origin,
    'https://paybis.com',
    process.env.PUBLIC_ONRAMP_WIDGET_URL,
    process.env.VUE_APP_AUTH_SERVICE_PUBLIC_URL,
    process.env.PAYBIS_API_HOST,
    process.env.PROCESSING_API_URL,
    process.env.CARD_PAYMENT_FRONT_URL,
    process.env.CARD_ENTRY_URL,
  ].map(el => {
    if (el && el.includes('/')) {
      const url = el
        .replaceAll('/', '\\/')
        .replaceAll('.', '\\.');
      return new RegExp(`^${url}`);
    }
    return el;
  }).filter((el): el is string | RegExp => Boolean(el));
}
